import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Home, BookOpen } from "lucide-react";
import "../../css/iaat/IAAT.css";
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import FAQIAATs from "./FAQIAATs";
import IAATExamples from "./IAATExamples";

export default function IAATPage() {
  const navigate = useNavigate();

  const handleBackToHome = () => {
    navigate("/", { replace: false });
    // Scroll to top after navigation
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="iaat-page-container">
      <Header />
      <main className="iaat-main-content">
      {/* Hero Section with SEO-optimized content */}
      <section className="iaat-hero-section">
        <div className="iaat-hero-content">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="iaat-hero-text"
          >
            <h1 className="iaat-main-title">Iowa Algebra Aptitude Test (IAAT) - Complete Guide & Practice Questions</h1>
            <p className="iaat-hero-description">
              The Iowa Algebra Aptitude Test (IAAT) is a standardized assessment designed to evaluate a student's readiness for Algebra I. Many school districts use this test to determine whether 6th-grade students are prepared to take Algebra I in 7th grade, ensuring they have the essential mathematical foundation needed for success in advanced mathematics.
            </p>
            <p className="iaat-hero-description">
              This free guide provides everything you need to understand the IAAT test format, practice with sample questions from all four key areas, and prepare your child for success. All practice materials are completely free and designed specifically for elementary and middle school students.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Areas Section */}
      <section className="iaat-areas-section">
        <div className="iaat-areas-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="iaat-areas-content"
          >
            <h2 className="iaat-section-title">IAAT Test Structure: Four Key Areas</h2>
            <p className="iaat-section-intro">
              The IAAT test is systematically organized into four main areas, each designed to assess different mathematical skills essential for Algebra I success. Understanding these areas helps students and parents prepare effectively for the test.
            </p>

            <div className="iaat-areas-grid">
              {/* Pre-Algebraic Number Skills */}
              <motion.div 
                className="iaat-area-card"
                whileHover={{ scale: 1.02, y: -4 }}
                transition={{ duration: 0.2 }}
              >
                <h3 className="iaat-area-title">
                  <span className="area-icon">📊</span>
                  Pre-Algebraic Number Skills
                </h3>
                <p className="iaat-area-description">
                  Tests fundamental math concepts including fractions, decimals, percentages, and basic operations that form the foundation for algebraic thinking. Students must demonstrate proficiency in number sense and computational skills.
                </p>
              </motion.div>

              {/* Interpreting Mathematical Information */}
              <motion.div 
                className="iaat-area-card"
                whileHover={{ scale: 1.02, y: -4 }}
                transition={{ duration: 0.2 }}
              >
                <h3 className="iaat-area-title">
                  <span className="area-icon">📈</span>
                  Interpreting Mathematical Information
                </h3>
                <p className="iaat-area-description">
                  Measures students' ability to analyze and extract meaningful information from graphs, charts, tables, and word problems. This area assesses data interpretation and mathematical reasoning skills.
                </p>
              </motion.div>

              {/* Representing Relationships */}
              <motion.div 
                className="iaat-area-card"
                whileHover={{ scale: 1.02, y: -4 }}
                transition={{ duration: 0.2 }}
              >
                <h3 className="iaat-area-title">
                  <span className="area-icon">🔢</span>
                  Representing Relationships
                </h3>
                <p className="iaat-area-description">
                  Evaluates the ability to recognize patterns, sequences, and numerical relationships. Students must identify rules and express relationships between numbers, preparing them for algebraic expressions and equations.
                </p>
              </motion.div>

              {/* Using Symbols */}
              <motion.div 
                className="iaat-area-card"
                whileHover={{ scale: 1.02, y: -4 }}
                transition={{ duration: 0.2 }}
              >
                <h3 className="iaat-area-title">
                  <span className="area-icon">➗</span>
                  Using Symbols
                </h3>
                <p className="iaat-area-description">
                  Assesses understanding of algebraic symbols, notation, and their correct usage in equations and expressions. Students must demonstrate knowledge of variables, operations, and the order of operations (PEMDAS).
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Test Overview Section */}
      <section className="iaat-overview-section">
        <div className="iaat-overview-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="iaat-overview-content"
          >
            <h2 className="iaat-section-title">IAAT Test Overview & Format</h2>
            <p className="iaat-overview-text">
              The Iowa Algebra Aptitude Test (IAAT) efficiently assesses a student's readiness for Algebra I in a single class period. The test provides valuable insights for educators to make informed placement decisions and identify students who may need additional support before entering Algebra I.
            </p>
            
            <div className="iaat-test-info-grid">
              <div className="iaat-info-card">
                <h4 className="info-card-title">⏱️ Test Duration</h4>
                <p className="info-card-text">50-60 minutes total, typically completed in one class period</p>
              </div>
              <div className="iaat-info-card">
                <h4 className="info-card-title">📝 Test Format</h4>
                <p className="info-card-text">Multiple-choice questions, primarily paper-based format with some districts using online versions</p>
              </div>
              <div className="iaat-info-card">
                <h4 className="info-card-title">📊 Scoring System</h4>
                <p className="info-card-text">Results are centrally processed for accuracy and consistency, providing percentile rankings and readiness scores</p>
              </div>
              <div className="iaat-info-card">
                <h4 className="info-card-title">🎯 Grade Levels</h4>
                <p className="info-card-text">Typically administered to 6th-grade students to determine readiness for 7th-grade Algebra I</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sample Questions Section */}
      <IAATExamples />

      {/* FAQ Section */}
      <FAQIAATs />

      {/* Call to Action Section */}
      <section className="iaat-cta-section">
        <div className="iaat-cta-container">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="iaat-cta-content"
          >
            <h2 className="iaat-cta-title">Ready to Practice for the IAAT Test?</h2>
            <p className="iaat-cta-text">
              Access thousands of free IAAT practice questions covering all four key areas. Create custom practice tests, track your progress, and download printable PDFs - all completely free!
            </p>
            <div className="iaat-cta-buttons">
              <a href="/account" className="iaat-cta-button iaat-cta-primary">
                <BookOpen size={20} />
                Start Practicing Free
              </a>
              <button onClick={handleBackToHome} className="iaat-cta-button iaat-cta-secondary">
                <Home size={20} />
                Back to Home
              </button>
            </div>
          </motion.div>
        </div>
      </section>
      </main>
      <Footer />
    </div>
  );
}
